/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-void */
/* eslint-disable no-console */
/* eslint-disable import/no-cycle */
/* eslint-disable max-len */
import React from "react";

import { getLocalValue, setLocalValue, removeLocalValue } from "../storage";

import { showErrorMessage } from "../hook/useDialogStateHelper";
import { UserDetailResponse, UserOrganization } from "../api/UserDetailService";
import { User } from "../api/UserSelectListService";
import { Company, fetchCompanySettings } from "../companies/Companies";

// コンテキストの型: 値のみ
export interface SompoContextValuesIF {
    homeVisited: boolean;
    userDetail: UserDetailResponse;
    userSummary: User;
    userImage: string;
    companyCode: string;
    companyName: string;
    companySettings: Company;
    searchText: string;
    loading: boolean;
    skillMapLoading: boolean;
    skillMapDrawerLoading: boolean;
}

// コンテキストの型: 更新機能付き
export interface SompoContextIF extends SompoContextValuesIF {
    setHomeVisited: (loading: boolean) => void;
    setUserDetail: (loginUser: UserDetailResponse) => Promise<void>;
    setUserSummary: (userSummary: User) => void;
    setUserImage: (userImage: string) => void;
    setSearchText: (serachText: string) => void;
    clear: () => void;
    setLoading: (loading: boolean) => void;
    setSkillMapLoading: (loading: boolean) => void;
    setSkillMapDrawerLoading: (loading: boolean) => void;
}

let hookContext: SompoContextIF;

export const getHookContext = () => hookContext;

// ローカルストレージにコンテキストを保存するためのキー
const KEY_LOCAL_STORAGE = "SompoContextValues";

// ローカルストレージのコンテキストを取得
const getStoredValues = () => getLocalValue(KEY_LOCAL_STORAGE);

// ローカルストレージにコンテキストを設定
const setStoredValues = (values: SompoContextValuesIF) => setLocalValue(KEY_LOCAL_STORAGE, JSON.stringify(values));

// ローカルストレージからコンテキストを除去
export const removeStoredValues = () => removeLocalValue(KEY_LOCAL_STORAGE);

const getDefaultContextValues = () => {
    const values = getStoredValues();
    return values
        ? (JSON.parse(values) as SompoContextValuesIF)
        : {
              homeVisited: false,
              userDetail: {} as UserDetailResponse,
              userSummary: {} as User,
              userImage: "",
              companyCode: "",
              companyName: "",
              searchText: "",
              loading: false,
              skillMapLoading: false,
              skillMapDrawerLoading: false,
              companySettings: {} as Company,
          };
};

const getDefaultContext = () => {
    const values = getDefaultContextValues();
    return {
        ...values,
        setHomeVisited: () => {},
        setUserDetail: async () => {},
        setUserSummary: () => {},
        setUserImage: () => {},
        setSearchText: () => {},
        setLoading: () => {},
        setSkillMapLoading: () => {},
        setSkillMapDrawerLoading: () => {},
        clear: () => {},
    };
};

// 実際のコンテキスト: 値のみ(コンポーネント外からの参照用)
export const SompoContextValues: SompoContextValuesIF = getDefaultContextValues();

// 実際のコンテキスト: 更新機能付き(コンポーネント内で利用)
const SompoContext = React.createContext<SompoContextIF>(getDefaultContext());

// コンテキストを更新するためのカスタムフック
export const useSompoContext = (): SompoContextIF => {
    // ローカルストレージから現在のコンテキストを取得
    const currentValues = getDefaultContextValues();

    // ホーム画面の表示実績
    const [homeVisited, setNewHomeVisited] = React.useState<boolean>(currentValues.homeVisited);
    const setHomeVisited = React.useCallback((newHomeVisited: boolean) => {
        setNewHomeVisited(newHomeVisited);
    }, []);

    // ログインユーザ(詳細)
    const [userDetail, setNewUserDetail] = React.useState<UserDetailResponse>(currentValues.userDetail);
    const [companyCode, setNewCompanyCode] = React.useState(currentValues.companyCode);
    const [companyName, setNewCompanyName] = React.useState(currentValues.companyName);
    const [companySettings, setNewCompanySettings] = React.useState(currentValues.companySettings);
    const setUserDetail = React.useCallback(
        async (newLoginUser: UserDetailResponse) => {
            setNewUserDetail(newLoginUser);
            const userOrganization: UserOrganization | undefined = (newLoginUser.userOrgs || []).find((organazation) => organazation.additionalFlg);

            const _companyCode = userOrganization?.companyId || "";
            setNewCompanyCode(_companyCode);
            setNewCompanyName(userOrganization?.orgName || "");

            // 表示設定の読み込み
            if (!_companyCode) return;
            const fetch = async () => {
                try {
                    const _companySettings = await fetchCompanySettings(_companyCode);
                    setNewCompanySettings(_companySettings);
                } catch (error: unknown) {
                    console.error(error);
                    showErrorMessage({ message: "表示設定の取得に失敗しました。", error });
                }
            };
            await fetch();
        },
        [userDetail]
    );

    // ローディングをここでグローバルなコンテキストとして持たせる
    // ローディングアニメーションの表示用
    const [loading, setNewLoading] = React.useState<boolean>(currentValues.loading);
    const setLoading = React.useCallback((newLoading: boolean) => {
        setNewLoading(newLoading);
    }, []);

    // スキルマップ用のローディングをここでグローバルなコンテキストとして持たせる
    // スキルマップのローディングアニメーションの表示用
    const [skillMapLoading, setNewSkillMapLoading] = React.useState<boolean>(currentValues.skillMapLoading);
    const setSkillMapLoading = React.useCallback((newLoading: boolean) => {
        setNewSkillMapLoading(newLoading);
    }, []);

    // スキルドロワ用のローディングをここでグローバルなコンテキストとして持たせる
    // スキルドロワのローディングアニメーションの表示用
    const [skillMapDrawerLoading, setNewSkillMapDrawerLoading] = React.useState<boolean>(currentValues.skillMapDrawerLoading);
    const setSkillMapDrawerLoading = React.useCallback((newLoading: boolean) => {
        setNewSkillMapDrawerLoading(newLoading);
    }, []);

    // ログインユーザ(サマリ)
    const [userSummary, setNewUserSummary] = React.useState<User>(currentValues.userSummary);
    const setUserSummary = React.useCallback((newUserSummary: User) => {
        setNewUserSummary(newUserSummary);
    }, []);

    // ログインユーザ(画像)
    const [userImage, setNewUserImage] = React.useState<string>(currentValues.userImage);
    const setUserImage = React.useCallback((newUserImage: string) => {
        setNewUserImage(newUserImage);
    }, []);

    // 検索テキスト
    const [searchText, setNewSearchText] = React.useState<string>(currentValues.searchText);
    const setSearchText = React.useCallback((newSearchText: string) => {
        setNewSearchText(newSearchText);
    }, []);

    // 新しいコンテキストを作成: 値のみ
    const values: SompoContextValuesIF = {
        // ログインユーザ
        homeVisited,
        userDetail,
        userSummary,
        userImage,
        companyCode,
        companyName,
        searchText,
        companySettings,
        loading,
        skillMapLoading,
        skillMapDrawerLoading,
    };

    // 新しいコンテキストを作成: 更新
    const newContext: SompoContextIF = {
        // 値をコピー
        ...values,

        // ホーム画面の表示実績
        setHomeVisited,

        // ログインユーザ
        setUserDetail,
        setUserSummary,
        setUserImage,

        // 検索テキスト
        setSearchText,

        // ローディング
        setLoading,
        setSkillMapLoading,
        setSkillMapDrawerLoading,

        // クリア
        clear: () => {
            setHomeVisited(false);
            setUserDetail({} as UserDetailResponse);
            setUserSummary({} as User);
            setUserImage("");
            setSearchText("");
            removeStoredValues();
        },
    };

    // 値のみのコンテキストを更新、ローカルストレージに保存(F5リロードの対策)
    Object.assign(SompoContextValues, values);
    setStoredValues(SompoContextValues);

    hookContext = newContext;

    // 全体のコンテキストを更新
    return newContext;
};

export default SompoContext;
